// The JavaScript side of the RoomPlan plugin.
//
// Two methods. `isSupported` before the button is drawn; `scan` when it is pressed. Anywhere
// but an iPhone Pro on iOS 17 the plugin is simply not there, and `Capacitor.isPluginAvailable`
// says so without a round trip.
import { registerPlugin, Capacitor } from '@capacitor/core'

/**
 * @typedef {{ cx, cy, cz, dx, dz, length, height, confidence: 'high'|'medium'|'low' }} WallSegment
 *   A wall, door or window on the floor, in millimetres: its centre, the unit direction it
 *   runs along, its length and its height. Same frame as the planner's plan -- x to the
 *   right, z toward you. For a door or a window, `cy` is the centre's height above the
 *   floor, so the sill is `cy - height / 2`.
 *
 * @typedef {{ cx, cy, cz, dx, dz, length, height, depth, category: string, confidence,
 *   storageType: 'cabinet'|'shelf'|null }} ScanObject
 *   Something standing in the room. `category` is RoomPlan's own name for it -- `storage`
 *   for cabinetry, plus `sink`, `stove`, `oven`, `dishwasher`, `refrigerator` and a dozen
 *   things that are not kitchen. Mapping those onto a maker's range is reproduce.js's job.
 *   `storageType` is iOS 17's second opinion about a `storage` object: fitted joinery, or a
 *   shelf, which is the difference between a run of cupboards and a bookcase.
 *
 * @typedef {{ cx, cz, dx, dz, length, depth, y }} BenchPatch
 *   A horizontal surface at bench height, in millimetres: a benchtop. `cx, cz` is its centre
 *   on the floor plane, `dx, dz` the unit direction it runs along, `length` its extent along
 *   that direction and `depth` across it, `y` how high the surface is above the floor.
 *
 *   This is the measurement RoomPlan's own classifier cannot make. It has one word for the
 *   whole of fitted joinery -- `storage` -- and it returns a continuous run of base cupboards
 *   as one or two low-confidence boxes or as nothing at all, so a kitchen full of cupboards
 *   came back as an empty room. The benchtop over them is a single unmistakable surface, and
 *   its extent along a wall IS the run's extent on that wall.
 *
 * @typedef {{ cx, cz, dx, dz, length, y0, y1 }} FrontPatch
 *   The same for a run of overhead doors: a vertical surface standing off a wall, `y0` and
 *   `y1` its bottom and top above the floor.
 *
 * @typedef {{ modelPath: string|null, meshPath: string|null, parametricPath: string|null }} ScanFiles
 *   The `file://` URLs of the 3D files a scan wrote into this device's cache. All three are
 *   best-effort and any of them can be null; the scan itself is never lost to a failed export.
 *
 *   `parametricPath` is RoomPlan's own USDZ: the room as Apple reconstructs it, with clean
 *   flat surfaces and generic stand-in furniture over the objects it recognised. Good for
 *   measuring off, and it carries NO camera colour at all -- opened on its own it is the white
 *   boxes that started all this.
 *
 *   `meshPath` is the room as it actually looks: the scanned mesh, with a colour on every
 *   vertex taken from the camera frames captured during the scan. This is what lets a cabinet
 *   maker see the kitchen that is there today -- the tiles, the timber, the doors coming out.
 *   Null on a scan that produced no scene mesh or no usable camera frames.
 *
 *   `modelPath` is whichever of those two is the one to show somebody: the coloured mesh when
 *   there is one, the parametric file otherwise. It is the key that has always been here and
 *   it still means the same thing, so nothing that already reads it needs to change.
 */

const RoomPlan = registerPlugin('RoomPlan')

/**
 * Whether this device can scan, and if not, WHICH of the reasons it is.
 *
 * A boolean was enough to decide whether to draw a button and not enough to say anything
 * useful when the answer was no -- a browser, an Android phone, and an iPhone without the
 * LiDAR sensor are three different conversations, and the app was having none of them.
 *
 * @returns {Promise<{ ok: boolean, reason: 'web'|'platform'|'hardware'|'missing'|null, platform: string }>}
 */
export async function scanStatus() {
  const platform = Capacitor.getPlatform()
  if (!Capacitor.isNativePlatform()) return { ok: false, reason: 'web', platform }
  if (platform !== 'ios') return { ok: false, reason: 'platform', platform }
  if (!Capacitor.isPluginAvailable('RoomPlan')) return { ok: false, reason: 'missing', platform }
  try {
    const r = await RoomPlan.isSupported()
    return r?.supported ? { ok: true, reason: null, platform } : { ok: false, reason: 'hardware', platform }
  } catch {
    return { ok: false, reason: 'missing', platform }
  }
}

/** True only on a phone that can actually scan. Kept: it is what the button's `v-if` wants. */
export async function canScan() {
  return (await scanStatus()).ok
}

/**
 * Put the scanner up and wait for Done.
 *
 * Everything RoomPlan found, not only the walls. The first version of this asked for
 * `walls` and dropped the rest, which is why scanning a kitchen full of cupboards came back
 * as an empty rectangle -- Apple had found the cabinets, the sink, the cooktop and the
 * doorway, and nothing asked for them.
 *
 * Resolves once the scan has been processed AND both 3D files have been written, which is a
 * few seconds after the scanner disappears: colouring a room mesh from the camera frames is
 * real arithmetic over a hundred thousand vertices. The scanning screen comes down first, so
 * the wait belongs to whatever the app shows next rather than to a frozen camera view.
 *
 * @returns {Promise<{ walls: WallSegment[], doors: WallSegment[], windows: WallSegment[],
 *   openings: WallSegment[], objects: ScanObject[], benches: BenchPatch[],
 *   fronts: FrontPatch[] } & ScanFiles | null>}
 *   `benches` and `fronts` are best-effort and routinely empty -- they come from ARKit's own
 *   planes or scene mesh, and a device or an iOS that gives us neither simply returns none.
 *   Everything downstream treats an empty list as "the classifier is all we have", which is
 *   what every scan before them was.
 *   `modelPath`, `meshPath` and `parametricPath` are the 3D files; see ScanFiles above. Null
 *   throughout if they cancelled.
 */
export async function scan() {
  try {
    const r = await RoomPlan.scan()
    return {
      walls: r?.walls ?? [],
      doors: r?.doors ?? [],
      windows: r?.windows ?? [],
      openings: r?.openings ?? [],
      objects: r?.objects ?? [],
      benches: r?.benches ?? [],
      fronts: r?.fronts ?? [],
      modelPath: r?.modelPath ?? null,
      meshPath: r?.meshPath ?? null,
      parametricPath: r?.parametricPath ?? null,
    }
  } catch (e) {
    if (/cancel/i.test(String(e?.message ?? e))) return null
    throw e
  }
}

export default RoomPlan
